import { createFileRoute } from "@tanstack/react-router";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/embedded-object-blanks")({
  head: () => ({
    meta: [
      { title: "Embedded Object Color-Cast Pen Blanks | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Acorn caps, sweet gum balls, mini pine cones, coffee beans and pasta cast in colored resin. Pen blanks at $12.95 each with tiered shipping.",
      },
      { property: "og:title", content: "Embedded object color-cast pen blanks" },
      {
        property: "og:description",
        content:
          "Real acorn caps, sweet gum balls, pine cones, coffee beans and pasta suspended in colored resin, cut to standard blank sizes.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: pageUrl("/embedded-object-blanks") },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: pageUrl("/embedded-object-blanks") }],
  }),
  component: EmbeddedObjectBlanks,
});

type Blank = {
  name: string;
  price: string;
  image: string;
  alt: string;
  spec: string;
};

const acornCaps: Blank[] = [
  {
    name: "Acorn cap — red",
    price: "$12.95",
    image: "/embed-acorn-cap-red.webp",
    alt: "Pen blank of acorn caps suspended in red resin",
    spec: "Acorn caps cast in red resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Acorn cap — red-blue",
    price: "$12.95",
    image: "/embed-acorn-cap-red-blue.webp",
    alt: "Pen blank of acorn caps suspended in red and blue resin",
    spec: "Acorn caps cast in red-blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Acorn cap — blue",
    price: "$12.95",
    image: "/embed-acorn-cap-blue.webp",
    alt: "Pen blank of acorn caps suspended in blue resin",
    spec: "Acorn caps cast in blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Acorn cap — green",
    price: "$12.95",
    image: "/embed-acorn-cap-green.webp",
    alt: "Pen blank of acorn caps suspended in green resin",
    spec: "Acorn caps cast in green resin. 3/4 x 3/4 x 5+ inches.",
  },
];

const sweetGum: Blank[] = [
  {
    name: "Sweet gum ball — red",
    price: "$12.95",
    image: "/embed-sweet-gum-ball-red.webp",
    alt: "Pen blank of sweet gum balls suspended in red resin",
    spec: "Sweet gum balls cast in red resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Sweet gum ball — red-blue",
    price: "$12.95",
    image: "/embed-sweet-gum-ball-red-blue.webp",
    alt: "Pen blank of sweet gum balls suspended in red and blue resin",
    spec: "Sweet gum balls cast in red-blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Sweet gum ball — blue",
    price: "$12.95",
    image: "/embed-sweet-gum-ball-blue.webp",
    alt: "Pen blank of sweet gum balls suspended in blue resin",
    spec: "Sweet gum balls cast in blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Sweet gum ball — green",
    price: "$12.95",
    image: "/embed-sweet-gum-ball-green.webp",
    alt: "Pen blank of sweet gum balls suspended in green resin",
    spec: "Sweet gum balls cast in green resin. 3/4 x 3/4 x 5+ inches.",
  },
];

const pineCones: Blank[] = [
  {
    name: "Pine cone — red",
    price: "$12.95",
    image: "/embed-pine-cone-red.webp",
    alt: "Pen blank of mini pine cones suspended in red resin",
    spec: "Mini pine cones cast in red resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pine cone — red-blue",
    price: "$12.95",
    image: "/embed-pine-cone-red-blue.webp",
    alt: "Pen blank of mini pine cones suspended in red and blue resin",
    spec: "Mini pine cones cast in red-blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pine cone — blue",
    price: "$12.95",
    image: "/embed-pine-cone-blue.webp",
    alt: "Pen blank of mini pine cones suspended in blue resin",
    spec: "Mini pine cones cast in blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pine cone — green",
    price: "$12.95",
    image: "/embed-pine-cone-green.webp",
    alt: "Pen blank of mini pine cones suspended in green resin",
    spec: "Mini pine cones cast in green resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pine cone — pearl white",
    price: "$12.95",
    image: "/embed-pine-cone-pearl-white.webp",
    alt: "Pen blank of mini pine cones suspended in pearl white resin",
    spec: "Mini pine cones cast in pearl white resin. 3/4 x 3/4 x 5+ inches.",
  },
];

const rotini: Blank[] = [
  {
    name: "Pasta rotini — red",
    price: "$12.95",
    image: "/embed-pasta-rotini-red.webp",
    alt: "Pen blank of tri-colored rotini pasta suspended in red resin",
    spec: "Tri colored rotini cast in red resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pasta rotini — red-blue",
    price: "$12.95",
    image: "/embed-pasta-rotini-red-blue.webp",
    alt: "Pen blank of tri-colored rotini pasta suspended in red and blue resin",
    spec: "Tri colored rotini cast in red-blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pasta rotini — blue",
    price: "$12.95",
    image: "/embed-pasta-rotini-blue.webp",
    alt: "Pen blank of tri-colored rotini pasta suspended in blue resin",
    spec: "Tri colored rotini cast in blue resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pasta rotini — green",
    price: "$12.95",
    image: "/embed-pasta-rotini-green.webp",
    alt: "Pen blank of tri-colored rotini pasta suspended in green resin",
    spec: "Tri colored rotini cast in green resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Pasta rotini — pearl white",
    price: "$12.95",
    image: "/embed-pasta-rotini-pearl-white.webp",
    alt: "Pen blank of tri-colored rotini pasta suspended in pearl white resin",
    spec: "Tri colored rotini cast in pearl white resin. 3/4 x 3/4 x 5+ inches.",
  },
];

const coffeeBeans: Blank[] = [
  {
    name: "Coffee bean — white",
    price: "$12.95",
    image: "/embed-coffee-bean-white.webp",
    alt: "Pen blank of coffee beans suspended in white resin",
    spec: "Coffee beans cast in white resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Coffee bean — cream",
    price: "$12.95",
    image: "/embed-coffee-bean-cream.webp",
    alt: "Pen blank of coffee beans suspended in cream resin",
    spec: "Coffee beans cast in cream resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Coffee bean — mocha",
    price: "$12.95",
    image: "/embed-coffee-bean-mocha.webp",
    alt: "Pen blank of coffee beans suspended in mocha resin",
    spec: "Coffee beans cast in mocha resin. 3/4 x 3/4 x 5+ inches.",
  },
  {
    name: "Coffee bean — mint",
    price: "$12.95",
    image: "/embed-coffee-bean-mint.webp",
    alt: "Pen blank of coffee beans suspended in mint resin",
    spec: "Coffee beans cast in mint resin. 3/4 x 3/4 x 5+ inches.",
  },
];

const elbows: Blank[] = [
  {
    name: "Pasta elbows — red",
    price: "$12.95",
    image: "/embed-pasta-elbows-red.webp",
    alt: "Pen blank of elbow pasta suspended in red resin",
    spec: "Elbow pasta cast in red resin. 7/8 x 5 inches.",
  },
  {
    name: "Pasta elbows — red-blue",
    price: "$12.95",
    image: "/embed-pasta-elbows-red-blue.webp",
    alt: "Pen blank of elbow pasta suspended in red and blue resin",
    spec: "Elbow pasta cast in red-blue resin. 7/8 x 5 inches.",
  },
  {
    name: "Pasta elbows — blue",
    price: "$12.95",
    image: "/embed-pasta-elbows-blue.webp",
    alt: "Pen blank of elbow pasta suspended in blue resin",
    spec: "Elbow pasta cast in blue resin. 7/8 x 5 inches.",
  },
  {
    name: "Pasta elbows — green",
    price: "$12.95",
    image: "/embed-pasta-elbows-green.webp",
    alt: "Pen blank of elbow pasta suspended in green resin",
    spec: "Elbow pasta cast in green resin. 7/8 x 5 inches.",
  },
];

const shells: Blank[] = [
  {
    name: "Pasta shells — red",
    price: "$12.95",
    image: "/embed-pasta-shells-red.webp",
    alt: "Pen blank of mini shell pasta suspended in red resin",
    spec: "Mini shell pasta cast in red resin. 7/8 x 5 inches.",
  },
  {
    name: "Pasta shells — red-blue",
    price: "$12.95",
    image: "/embed-pasta-shells-red-blue.webp",
    alt: "Pen blank of mini shell pasta suspended in red and blue resin",
    spec: "Mini shell pasta cast in red-blue resin. 7/8 x 5 inches.",
  },
  {
    name: "Pasta shells — blue",
    price: "$12.95",
    image: "/embed-pasta-shells-blue.webp",
    alt: "Pen blank of mini shell pasta suspended in blue resin",
    spec: "Mini shell pasta cast in blue resin. 7/8 x 5 inches.",
  },
  {
    name: "Pasta shells — green",
    price: "$12.95",
    image: "/embed-pasta-shells-green.webp",
    alt: "Pen blank of mini shell pasta suspended in green resin",
    spec: "Mini shell pasta cast in green resin. 7/8 x 5 inches.",
  },
];

function BlankGrid({ items }: { items: Blank[] }) {
  return (
    <ul className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
      {items.map((b) => (
        <li key={b.name} className="flex flex-col bg-background">
          <div className="overflow-hidden bg-paper">
            <img
              src={b.image}
              alt={b.alt}
              loading="lazy"
              width={360}
              height={271}
              className="h-full w-full object-cover motion-craft transition-transform duration-700 hover:scale-[1.03]"
            />
          </div>
          <div className="flex flex-1 flex-col p-7 sm:p-8">
            <h3 className="font-[family-name:var(--font-display)] text-2xl leading-tight">{b.name}</h3>
            <p className="mt-2 text-lg font-semibold text-walnut">{b.price}</p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{b.spec}</p>
            <div className="mt-auto pt-6">
              <CtaLink to="/contact" variant="outline" className="h-11">
                Order this blank
              </CtaLink>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

const shipping = "Each blank is $12.95. Shipping is $6 for 1 to 5, $11 for 6 to 25, and $19 for 26 or more.";

function EmbeddedObjectBlanks() {
  return (
    <>
      <Breadcrumbs
        trail={[
          { label: "Home", to: "/" },
          { label: "For Penturners", to: "/for-pen-turners" },
          { label: "Embedded Object Blanks" },
        ]}
      />
      <PageHeader
        eyebrow="Products for penturners"
        title="Embedded object color-cast blanks."
        lede="Acorn caps, sweet gum balls, pine cones and pasta cast in colored resin. Shipping is $6 for 1 to 5, $11 for 6 to 25, and $19 for 26 or more."
      >
        <CtaLink to="/contact" variant="outline" className="h-12">
          Ask about availability
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="acorn-heading">
        <div className="shell">
          <SectionHeading
            id="acorn-heading"
            eyebrow="Acorn caps"
            title="Acorn caps in four colours."
            lede={shipping}
          />
          <BlankGrid items={acornCaps} />
        </div>
      </Section>

      <Section labelledBy="gum-heading">
        <div className="shell">
          <SectionHeading
            id="gum-heading"
            eyebrow="Sweet gum balls"
            title="Sweet gum balls, spiked and suspended."
            lede={shipping}
          />
          <BlankGrid items={sweetGum} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="cone-heading">
        <div className="shell">
          <SectionHeading
            id="cone-heading"
            eyebrow="Mini pine cones"
            title="Mini pine cones in five colours."
            lede={shipping}
          />
          <BlankGrid items={pineCones} />
        </div>
      </Section>

      <Section labelledBy="coffee-heading">
        <div className="shell">
          <SectionHeading
            id="coffee-heading"
            eyebrow="Coffee beans"
            title="Roasted beans in warm resin tones."
            lede={shipping}
          />
          <BlankGrid items={coffeeBeans} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="rotini-heading">
        <div className="shell">
          <SectionHeading
            id="rotini-heading"
            eyebrow="Pasta rotini"
            title="Tri-coloured rotini, cut on the spiral."
            lede={shipping}
          />
          <BlankGrid items={rotini} />
        </div>
      </Section>

      <Section labelledBy="elbows-heading">
        <div className="shell">
          <SectionHeading
            id="elbows-heading"
            eyebrow="Pasta elbows"
            title="Elbow pasta in 7/8 inch rounds."
            lede={shipping}
          />
          <BlankGrid items={elbows} />
        </div>
      </Section>

      <Section tone="paper" labelledBy="shells-heading">
        <div className="shell">
          <SectionHeading
            id="shells-heading"
            eyebrow="Pasta shells"
            title="Mini shells in 7/8 inch rounds."
            lede={shipping}
          />
          <BlankGrid items={shells} />
          <p className="mt-8 max-w-2xl text-sm text-muted-foreground">
            Turning a batch for a club or a class? Tell Paul how many you need and he will quote it with the right
            shipping tier applied.
          </p>
        </div>
      </Section>

      <FinalCTA
        heading="Want your own object cast in resin?"
        body="Send Paul the item and he will cast and cut it into a blank ready for the lathe."
        label="Start a conversation"
      />
    </>
  );
}
